// components/common/InfoModal.jsx
import React, { useState, useRef, useEffect } from 'react';

const InfoModal = ({ isOpen, onClose, title = "Information", children }) => {
  const [showScrollHint, setShowScrollHint] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    if (isOpen && contentRef.current) {
      // Check if content is scrollable
      const isScrollable = contentRef.current.scrollHeight > contentRef.current.clientHeight;
      setShowScrollHint(isScrollable);
    }
  }, [isOpen, children]);

  const handleScroll = (e) => {
    const element = e.target;
    const isAtBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 10;
    if (isAtBottom) {
      setShowScrollHint(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Dark Overlay */}
      <div
        className="modal-overlay"
        onClick={onClose}
        style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1050
        }}
      />

      {/* Modal Wrapper */}
      <div
        className="modal-content-wrapper"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'min(800px, 92vw)',
          maxHeight: '88vh',
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--white)',
          borderRadius: 'var(--rounded-16)',
          boxShadow: 'var(--shadow-3)',
          zIndex: 1060,
          overflow: 'hidden'
        }}
      >
        {/* Header - Fixed */}
        <div 
          className="d-flex align-items-center justify-content-between"
          style={{
            padding: 'clamp(1.25rem, 4vw, 1.75rem)',
            borderBottom: '1px solid var(--border-color)',
            flexShrink: 0
          }}
        >
          <h6 
            className="text-primary-light fw-bold mb-0" 
            style={{ 
              fontSize: 'clamp(1.25rem, 3vw, 1.625rem)',
              lineHeight: '1.3',
              paddingRight: '1rem'
            }}
          >
            {title}
          </h6>
        </div>

        {/* Body - Scrollable (Hidden Scrollbar) */}
        <div 
          ref={contentRef}
          onScroll={handleScroll}
          className="modal-body-content text-secondary-light"
          style={{
            padding: 'clamp(1.25rem, 4vw, 2rem)',
            overflowY: 'auto',
            flex: 1,
            fontSize: 'clamp(0.9rem, 2vw, 1rem)',
            lineHeight: '1.6',
            position: 'relative',
            // Hide scrollbar for all browsers
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          {children}
          
          {/* Scroll Hint - Fade Gradient at Bottom */}
           {showScrollHint && (
            <div
              style={{
                position: 'sticky',
                bottom: 0,
                left: 0,
                right: 0,
                height: '80px',
                marginTop: '-80px',
                // background: 'linear-gradient(to bottom, transparent, var(--white))',
                pointerEvents: 'none',
                zIndex: 1
              }}
            />
          )}
        </div>

        {/* Scroll Indicator Icon */}
        {showScrollHint && (
          <div
            className="scroll-hint-wrapper"
            style={{
              position: 'absolute',
              bottom: 'clamp(5.5rem, 12vw, 6.5rem)',
              left: '50%',
              zIndex: 2,
              pointerEvents: 'auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer'
            }}
            onClick={() => {
              contentRef.current?.scrollBy({ top: 200, behavior: 'smooth' });
            }}
          >
            <div
              className="scroll-hint-icon"
              style={{
                background: 'var(--primary-600)',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
                transition: 'all 0.3s ease'
              }}
            >
              <i 
                className="ri-arrow-down-s-line text-base" 
                style={{ 
                  fontSize: '20px', 
                  lineHeight: 1
                }}
              ></i>
            </div>
            <span
              className="scroll-hint-text"
              style={{
                fontSize: '0.75rem',
                fontWeight: '500',
                color: 'var(--primary-600)',
                opacity: 0,
                transition: 'all 0.3s ease',
                whiteSpace: 'nowrap',
                background: 'var(--white)',
                padding: '4px 12px',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                pointerEvents: 'none'
              }}
            >
              Scroll for more
            </span>
          </div>
        )}

        {/* Footer - Fixed */}
        <div 
          style={{
            padding: 'clamp(1rem, 3vw, 1.5rem) clamp(1.25rem, 4vw, 2rem)',
            borderTop: '1px solid var(--border-color)',
            textAlign: 'right',
            flexShrink: 0,
          }}
        >
          <button
            onClick={onClose}
            className="btn bg-primary-600 text-base rounded-8 fw-medium hover-bg-primary-700"
            style={{
              padding: 'clamp(0.625rem, 2vw, 0.75rem) clamp(1.5rem, 4vw, 2rem)',
              fontSize: 'clamp(0.875rem, 2vw, 1rem)',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 8px rgba(var(--primary-600-rgb, 37, 99, 235), 0.2)'
            }}
          >
            Close
          </button>
        </div>
      </div>

      {/* Global Styles */}
      <style jsx>{`
        .modal-body-content::-webkit-scrollbar {
          display: none;
        }

        .scroll-hint-wrapper {
          animation: bounce 2s infinite;
          transform: translateX(-50%);
        }

        .scroll-hint-wrapper:hover {
          animation: none;
        }

        .scroll-hint-wrapper:hover .scroll-hint-icon {
          transform: scale(1.1);
          box-shadow: 0 6px 16px rgba(37, 99, 235, 0.4);
        }

        .scroll-hint-wrapper:hover .scroll-hint-text {
          opacity: 1;
        }

        @keyframes bounce {
          0%, 100% {
            transform: translateX(-50%) translateY(0);
          }
          50% {
            transform: translateX(-50%) translateY(-8px);
          }
        }
      `}</style>
    </>
  );
};

export default InfoModal;